import { useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Button from './Button'
import Modal from './Modal'
import { API } from 'aws-amplify'
import { createForm } from '../api/mutations'
import { useNotificationContext } from '../context/notification-context'
import {
  ATS_MAP,
  ATS_OPTIONS,
  HIRING_PLAN_MAP,
  HIRING_PLAN_OPTIONS,
  JOB_TYPES,
  VOLUME_MAP,
  VOLUME_OPTIONS,
  groesseFromEmployees,
  infoDichteFromJobs
} from '../helper/leadQualification'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Keine valide E-Mail-Adresse')
    .required('E-Mail-Adresse erforderlich')
})

const inputClass =
  'block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'

const labelClass = 'block text-xs font-medium text-gray-700 mb-1'

const CreateCompanyModal = ({ open, setOpen }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { success } = useNotificationContext()

  const createCompany = async (values) => {
    try {
      setIsLoading(true)

      const info = infoDichteFromJobs(values.jobTypes)
      const volume = VOLUME_MAP[values.applicationVolume] ?? { enum: 'LOW' }
      const ats = ATS_MAP[values.ats] ?? { enum: 'UNBEKANNT' }
      const employees =
        values.employees !== '' && values.employees != null
          ? Number(values.employees)
          : null
      const groesse = groesseFromEmployees(employees)

      await API.graphql({
        query: createForm,
        variables: {
          input: {
            form_source: 'FUNNEL_LEAD_QUALIFICATION',
            status: 'RECEIVED',
            lead_progress: 100,
            last_activity: 'ADMIN_CREATED_VIA_OS_DASHBOARD',
            last_activity_time: new Date().toISOString(),
            data: JSON.stringify({
              EMAIL: values.email.toLowerCase(),
              language: values.language || 'de',
              quali_info_dichte: info.enum,
              quali_hiring_volume: volume.enum,
              quali_anzahl_stellen: HIRING_PLAN_MAP[values.hiringPlan] ?? null,
              quali_ats: ats.enum,
              quali_unternehmensgroesse: groesse.enum,
              quali_stellentyp_auswahl_raw: (values.jobTypes || []).join(';'),
              brand_name: values.brandName || null,
              brand_employees: employees,
              ref_link: '',
              createdViaAdmin: true,
              adminSource: 'OS_DASHBOARD'
            }),
            lastUpdatedBy: 'OS_DASHBOARD_ADMIN'
          }
        }
      })

      success(
        'Kunde angelegt — HubSpot-Sync, Trial-Partner und Welcome-Mail laufen im Hintergrund.'
      )
    } catch (error) {
      console.error(error) // eslint-disable-line
    } finally {
      setIsLoading(false)
      setOpen(false)
    }
  }

  return (
    <Modal open={open} setOpen={setOpen}>
      <Formik
        initialValues={{
          email: '',
          language: 'de',
          applicationVolume: '',
          hiringPlan: '',
          ats: 'weiss-nicht',
          employees: '',
          brandName: '',
          jobTypes: []
        }}
        onSubmit={createCompany}
        validationSchema={validationSchema}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <div className='text-center'>
              <span className='text-base font-semibold leading-6 text-gray-900'>
                Neuen Kunden hinzufügen
              </span>
              <p className='text-sm text-gray-500 mt-1'>
                Quali-Felder optional — werden für Lead-Scoring & HubSpot
                genutzt.
              </p>
            </div>

            <div className='mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1'>
              <div>
                <label htmlFor='email' className={labelClass}>
                  E-Mail *
                </label>
                <Field
                  type='email'
                  name='email'
                  id='email'
                  className={inputClass}
                  placeholder='new@customer.com'
                />
                {errors.email && touched.email ? (
                  <div className='text-xs text-red-600 mt-1'>
                    {errors.email}
                  </div>
                ) : null}
              </div>

              <div>
                <label htmlFor='language' className={labelClass}>
                  Sprache
                </label>
                <Field
                  as='select'
                  name='language'
                  id='language'
                  className={inputClass}
                >
                  <option value='de'>Deutsch</option>
                  <option value='en'>English</option>
                </Field>
              </div>

              <div>
                <label htmlFor='brandName' className={labelClass}>
                  Firmenname
                </label>
                <Field
                  type='text'
                  name='brandName'
                  id='brandName'
                  className={inputClass}
                  placeholder='Acme GmbH'
                />
              </div>

              <div>
                <label htmlFor='employees' className={labelClass}>
                  Mitarbeitende (Anzahl)
                </label>
                <Field
                  type='number'
                  name='employees'
                  id='employees'
                  min='0'
                  className={inputClass}
                  placeholder='z. B. 250'
                />
              </div>

              <div>
                <label htmlFor='applicationVolume' className={labelClass}>
                  Bewerbungslage
                </label>
                <Field
                  as='select'
                  name='applicationVolume'
                  id='applicationVolume'
                  className={inputClass}
                >
                  <option value=''>— unbekannt —</option>
                  {VOLUME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <label htmlFor='hiringPlan' className={labelClass}>
                  Anzahl offener Stellen
                </label>
                <Field
                  as='select'
                  name='hiringPlan'
                  id='hiringPlan'
                  className={inputClass}
                >
                  <option value=''>— unbekannt —</option>
                  {HIRING_PLAN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <label htmlFor='ats' className={labelClass}>
                  ATS / Bewerbermanagement vorhanden?
                </label>
                <Field as='select' name='ats' id='ats' className={inputClass}>
                  {ATS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <span className={labelClass}>
                  Stellentypen (Mehrfachauswahl)
                </span>
                <div className='flex flex-wrap gap-1.5'>
                  {JOB_TYPES.map((jt) => {
                    const active = values.jobTypes.includes(jt)
                    return (
                      <button
                        key={jt}
                        type='button'
                        onClick={() => {
                          const next = active
                            ? values.jobTypes.filter((x) => x !== jt)
                            : [...values.jobTypes, jt]
                          setFieldValue('jobTypes', next)
                        }}
                        className={`text-xs px-2 py-1 rounded-full border ${
                          active
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                        }`}
                      >
                        {jt}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className='mt-5 sm:mt-6'>
              <Button.PrimaryLG
                text={isLoading ? 'lädt ...' : 'Jetzt einladen'}
                fullWidth
                isLoading={isSubmitting}
                type='submit'
                disabled={
                  !touched.email || errors.email || isLoading || isSubmitting
                }
              />
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default CreateCompanyModal
